import React, { MutableRefObject, useEffect, useMemo, useRef, useState } from 'react'
import { Flex, Text, TextVariant, Modal, useScrollVisible, TableSquare, TableColumn } from '@mprolab/uikit'
import { MediaQuery } from '@mprolab/uikit/src/theme/base'
import { BNToNumberWithDecimals, isArrayPopulated } from '@mprolab/sdk'
import styled, { useTheme } from 'styled-components'
import { useTranslation } from 'react-i18next'
import { Grid } from '@mui/material'
import dayjs from 'dayjs'
import { useMproStakeRewardContract } from 'hooks/contracts/useMproStakeRewardContract'
import { useWeb3React } from 'lib/wagmi-compat/useWeb3React'
import { logMessage } from 'utils/logMessage'

const ContentWrapper = styled(Flex)`
  flex-direction: column;
  margin-top: 32px;
  width: 499px;

  ${({ theme }) => theme.mediaQueries.mobile} {
    width: 100%;
  }
`

const RowText = styled(Text)<{ header?: boolean }>`
  color: ${({ theme, header }) => (header ? theme.colors.gray6 : theme.colors.white)};
`

const List = styled(Flex)<{ scrollable: boolean }>`
  overflow-y: auto;
  max-height: 483px;
  flex-direction: column;
  padding-right: ${({ scrollable }) => (scrollable ? '16px' : 'unset')};

  ${({ theme }) => theme.mediaQueries.tablet} {
    max-height: 390px;
  }
`

const Footer = styled(Flex)`
  padding: 8px 24px;
  flex-direction: column;
  margin-top: 32px;
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.gray10};
`

interface ClaimHistoryProps {
  onDismiss?: () => void
  strakeContractAddress: string
}

interface StakedData {
  updatedAt: number
  updatedAmount: number
}

const ClaimHistory: React.FC<ClaimHistoryProps> = ({ onDismiss, strakeContractAddress }) => {
  const { account } = useWeb3React()
  const { t } = useTranslation()
  const { colors } = useTheme()
  const wrapper = useRef<HTMLDivElement>(null)
  const scrollVisible = useScrollVisible(wrapper as MutableRefObject<HTMLDivElement>)
  const { getWalletStakeUpdates } = useMproStakeRewardContract(strakeContractAddress)
  const [stakeHistory, setStakeHistory] = useState<StakedData[]>([])
  const [isLoading, setIsLoading] = useState(true)

  const sumNumbers = (numbers: number[]): number => {
    return numbers.reduce((total, number) => total + number, 0)
  }

  const columns = useMemo(
    (): TableColumn[] => [
      {
        key: 'date',
        name: (
          <RowText variant={TextVariant.BODY_SMALL} header>
            {t('common.date')}
          </RowText>
        ),
        gridColumns: 6,
        customComponent(data: StakedData) {
          return <RowText variant={TextVariant.BODY_SMALL}>{data.updatedAt}</RowText>
        }
      },
      {
        key: 'amount',
        name: (
          <RowText variant={TextVariant.BODY_SMALL} header pl="4px">
            {`${t('common.amount')} [MPRO]`}
          </RowText>
        ),
        gridColumns: 6,
        customComponent(data: StakedData) {
          return <RowText variant={TextVariant.BODY_SMALL_BOLD}>{data.updatedAmount}</RowText>
        }
      }
    ],
    [stakeHistory]
  )

  useEffect(() => {
    const fetchData = async () => {
      try {
        const stakeData = await getWalletStakeUpdates(account)
        const data: StakedData[] = stakeData
          .map((item: any) => ({
            updatedAt: dayjs.unix(item._blockTimestamp.toNumber()).format('MM/DD/YY'),
            updatedAmount: BNToNumberWithDecimals(item._updatedAmount, 18, 6)
          }))
          .sort((a: StakedData, b: StakedData) => {
            const dateA = new Date(a.updatedAt)
            const dateB = new Date(b.updatedAt)

            // Sort in descending order (newest first)
            return dateB.getTime() - dateA.getTime()
          })

        setStakeHistory(data)
        setIsLoading(false)
      } catch (error) {
        logMessage(`Failed to fetch stake history: ${error}`)
      }
    }
    fetchData()
  }, [account])

  return (
    <Modal onDismiss={onDismiss} title={t('stakingPanel.stakeHistory')}>
      <ContentWrapper>
        <List ref={wrapper} scrollable={scrollVisible}>
          <TableSquare
            data={stakeHistory}
            loading={isLoading}
            plain
            fullViewMediaquery={MediaQuery.tablet}
            fullViewCustomWidth={100}
            {...{ columns }}
            emptyListComponent={<Text textAlign="center">{t('stakingPanel.noStakeHistory')}</Text>}
          />
        </List>
        {isArrayPopulated(stakeHistory) && (
          <Footer>
            <Grid container>
              <Grid item xs={6} />
              <Grid item xs={6}>
                <Flex flexDirection="column" gap="4px">
                  <Text variant={TextVariant.BODY_SMALL} color={colors.gray6}>{`${t(
                    'common.totalAmount'
                  )} [MPRO]`}</Text>
                  <Text variant={TextVariant.BODY_SMALL_BOLD}>
                    {sumNumbers(stakeHistory.map(({ updatedAmount }) => updatedAmount)).toFixed(6)}
                  </Text>
                </Flex>
              </Grid>
            </Grid>
          </Footer>
        )}
      </ContentWrapper>
    </Modal>
  )
}

export default ClaimHistory
